import { useEffect, useState } from "react";
import Pagination from "../../components/common/pagination/Pagination";
import Title from "../../components/common/title/Title";
import Button from "../../components/common/button/Button";
import { getApi, putApi, deleteApi } from "../../services/api/globalApi";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { utilsState } from "../../sotre/redux/features/utilsSlice";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { RxDragHandleDots2 } from "react-icons/rx";
import {
  successMessage,
  errorMessage,
} from "../../utils/toastify-message/ToastifyMessage";

function SortableFaqItem({ item, index, onEdit, onDelete, partName }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
    >
      <td className="px-2 sm:px-4 py-3">
        <button
          {...attributes}
          {...listeners}
          type="button"
          className="cursor-grab text-gray-500 hover:text-gray-700"
        >
          <RxDragHandleDots2 size={18} />
        </button>
      </td>
      <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm">{item.title}</td>
      <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm">
        {partName || "-"}
      </td>
      <td className="px-2 sm:px-4 py-3 text-center">{index + 1}</td>
      <td className="px-2 sm:px-4 py-3">
        <div className="flex gap-1 sm:gap-2">
          <button
            onClick={() => onEdit(item)}
            className="text-xs sm:text-sm font-medium text-blue-600 dark:text-blue-500 hover:underline"
          >
            ویرایش
          </button>
          <button
            onClick={() => onDelete(item)}
            className="text-xs sm:text-sm font-medium text-red-600 dark:text-red-500 hover:underline"
          >
            حذف
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function FaqList() {
  const [faqData, setFaqData] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor));
  const { utils } = useSelector(utilsState);

  const getCategory = async (params) => {
    const path = "admin/faq";
    try {
      const response = await getApi({ path, params: { ...params } });
      const dataWithIds = response?.data?.result.data.map((item) => ({
        ...item,
        id: item._id,
      }));
      setFaqData(dataWithIds);
      setPagination(response?.data?.result);
      setHasChanges(false);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getCategory({});
  }, []);

  const [filter, setFilter] = useState();

  const filterDataHandler = (newParams) => {
    setFilter({ ...filter, ...newParams });
    const allFilter = {
      ...filter,
      ...newParams,
    };
    console.log("filter", allFilter);
    getCategory(allFilter);
  };

  const navigate = useNavigate();

  const editCategory = (singleCategory) => {
    navigate("/edit-faq", { state: singleCategory });
  };

  const addFaq = () => {
    navigate("/add-faq");
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = faqData.findIndex((item) => item.id === active.id);
      const newIndex = faqData.findIndex((item) => item.id === over?.id);
      setFaqData((items) => arrayMove(items, oldIndex, newIndex));
      setHasChanges(true);
    }
  };

  const savePriorities = async () => {
    try {
      const items = faqData.map((item, index) => ({
        id: item._id,
        priority: faqData.length - index,
      }));

      const path = "admin/faq/update-priorities";
      await putApi({ path, body: { items } });
      successMessage("اولویت‌ها با موفقیت ذخیره شد");
      setHasChanges(false);
      getCategory({});
    } catch (error) {
      console.log(error);
      errorMessage("خطا در ذخیره اولویت‌ها");
    }
  };

  const deleteFaq = async (faq) => {
    if (window.confirm(`آیا از حذف "${faq.title}" اطمینان دارید؟`)) {
      try {
        const path = `admin/faq/${faq._id}`;
        await deleteApi({ path });
        successMessage("سوال با موفقیت حذف شد");
        getCategory({});
      } catch (error) {
        console.log(error);
        errorMessage("خطا در حذف سوال");
      }
    }
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-3xl p-6">
      <div className="flex justify-between items-center mb-6 gap-4">
        <Title title={"لیست سوالات"} />
        <div className="flex gap-2">
          {hasChanges && (
            <Button
              onClick={savePriorities}
              text="ذخیره اولویت‌ها"
              color="primary"
              type="button"
              className="whitespace-nowrap min-w-fit"
            />
          )}
          <Button
            onClick={addFaq}
            text="افزودن سوال"
            color="success"
            type="button"
            className="whitespace-nowrap min-w-fit"
          />
        </div>
      </div>

      <Pagination
        pagination={pagination}
        filterDataHandler={filterDataHandler}
      />

      <div className="relative overflow-x-auto shadow-md sm:rounded-lg mt-6">
        {faqData?.length ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <table className="w-full text-xs sm:text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th scope="col" className="px-2 sm:px-4 py-2 sm:py-3 w-8"></th>
                  <th scope="col" className="px-2 sm:px-4 py-2 sm:py-3">
                    عنوان
                  </th>
                  <th scope="col" className="px-2 sm:px-4 py-2 sm:py-3">
                    بخش
                  </th>
                  <th
                    scope="col"
                    className="px-2 sm:px-4 py-2 sm:py-3 text-center w-16"
                  >
                    اولویت
                  </th>
                  <th scope="col" className="px-2 sm:px-4 py-2 sm:py-3">
                    عملیات
                  </th>
                </tr>
              </thead>
              <SortableContext
                items={faqData.map((item) => item.id)}
                strategy={verticalListSortingStrategy}
              >
                <tbody>
                  {faqData?.map((item, index) => {
                    const partName = utils?.FAQ_PART?.[item.part]?.name || "";
                    return (
                      <SortableFaqItem
                        key={item.id}
                        item={item}
                        index={index}
                        onEdit={editCategory}
                        onDelete={deleteFaq}
                        partName={partName}
                      />
                    );
                  })}
                </tbody>
              </SortableContext>
            </table>
          </DndContext>
        ) : (
          <div className="w-full flex items-center justify-center my-3 py-2 dark:text-white">
            <p>هیچ سوالی هنوز ساخته نشده است.</p>
          </div>
        )}
      </div>

      <Pagination
        pagination={pagination}
        filterDataHandler={filterDataHandler}
      />
    </div>
  );
}
